import { useState } from "react";
import { View, StyleSheet, ActivityIndicator, Linking } from "react-native";
import * as DocumentPicker from "expo-document-picker";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { useStorage } from "@/hooks/useStorage";
import { supabase } from "@/lib/supabase-client";
import { AttachmentFile } from "@/components/Attachment/types";
import { useFiles } from "./hooks/useFiles";
import { FilesHeader } from "./FilesHeader";
import { EmptyState } from "./components/EmptyState";
import { ErrorBanner } from "./components/ErrorBanner";
import { ImagePreview } from "./components/ImagePreview";
import { IconsView } from "./views/IconsView";
import { ListView } from "./views/ListView";
import { ColumnsView } from "./views/ColumnsView";
import { FilesTabProps, ViewMode } from "./types";

export { FilesTabProps } from "./types";

const STORAGE_KEY = "files-view-mode";
const VALID_MODES: ViewMode[] = ["icons", "list", "columns"];

export function FilesTab({
  entityType,
  entityId,
  loading: entityLoading = false,
  canUpload = true,
  canRename = true,
  canDelete = true, //TODO pass permisions from DrawerBody, leaving all true for now
}: FilesTabProps) {
  const { colors } = useTheme();
  const { getString, saveFast } = useStorage();

  // Load initial view mode from storage
  const getInitialViewMode = (): ViewMode => {
    const saved = getString(STORAGE_KEY);
    if (saved && VALID_MODES.includes(saved as ViewMode)) {
      return saved as ViewMode;
    }
    return "icons";
  };

  const [viewMode, setViewMode] = useState<ViewMode>(getInitialViewMode);
  const [expandedImage, setExpandedImage] = useState<{ url: string; name: string } | null>(null);

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    saveFast(STORAGE_KEY, mode);
  };
  const { files, loading, error, uploadFiles, removeFile, renameFile, clearError } = useFiles(entityType, entityId);

  const handleUpload = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        multiple: true,
        copyToCacheDirectory: true,
      });

      if (result.canceled) return;

      // Convert DocumentPicker assets to File objects
      const filesToUpload: File[] = [];
      for (const asset of result.assets) {
        const response = await fetch(asset.uri);
        const blob = await response.blob();
        const file = new File([blob], asset.name, { type: asset.mimeType || "application/octet-stream" });
        filesToUpload.push(file);
      }

      await uploadFiles(filesToUpload);
    } catch (err) {
      console.error("Error picking documents:", err);
    }
  };

  const handleExpandImage = (url: string, name: string) => {
    setExpandedImage({ url, name });
  };

  const handleFilePreview = async (file: AttachmentFile) => {
    const isImage = file.category === "image" || file.type?.startsWith("image/");
    if (!isImage) return;

    let url: string | null = null;
    if (file.bucket && file.uri) {
      const { data } = await supabase.storage.from(file.bucket).createSignedUrl(file.uri, 3600);
      url = data?.signedUrl ?? null;
    } else if (file.uri) {
      url = file.uri;
    }

    if (url) {
      setExpandedImage({ url, name: file.name });
    }
  };

  // No entity selected state
  if (!entityId) {
    return (
      <View style={styles.container}>
        <View style={[styles.messageContainer, { backgroundColor: colors.backgroundSecondary }]}>
          <ThemedText style={[styles.messageText, { color: colors.textSecondary }]}>
            No entity selected. Please select an entity to view files.
          </ThemedText>
        </View>
      </View>
    );
  }

  // Initial loading state
  if ((loading && files.length === 0) || entityLoading) {
    return (
      <View style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <ThemedText style={[styles.loadingText, { color: colors.textSecondary }]}>Loading files...</ThemedText>
        </View>
      </View>
    );
  }

  const ViewComponent = {
    icons: IconsView,
    list: ListView,
    columns: ColumnsView,
  }[viewMode];

  return (
    <View style={styles.container}>
      {expandedImage ? (
        <ImagePreview
          url={expandedImage.url}
          name={expandedImage.name}
          onClose={() => setExpandedImage(null)}
          onOpenInNewTab={() => Linking.openURL(expandedImage.url)}
        />
      ) : (
        <>
          {/* Error Banner */}
          {error && <ErrorBanner message={error} onDismiss={clearError} />}

          {/* Header with Upload + View Toggle */}
          <FilesHeader
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
            onUpload={canUpload ? handleUpload : undefined}
            uploading={loading}
          />

          {/* Content */}
          {files.length === 0 ? (
            <EmptyState entityType={entityType} />
          ) : (
            <ViewComponent
              files={files}
              onFileRemove={canDelete ? removeFile : undefined}
              onFileRename={canRename ? renameFile : undefined}
              onExpandImage={handleExpandImage}
              onFilePreview={handleFilePreview}
            />
          )}
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  messageContainer: {
    padding: 32,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  messageText: {
    fontSize: 14,
    textAlign: "center",
    lineHeight: 20,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    gap: 12,
  },
  loadingText: {
    fontSize: 14,
  },
});
